/**
 * Simple Frontmatter Parser
 * 
 * Browser-compatible parser for YAML frontmatter in markdown files.
 * Does not require Node.js polyfills.
 */

/**
 * Parse frontmatter from markdown content
 * Returns { data, content } where data is the frontmatter object
 */
export const parseFrontmatter = (markdown) => {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = markdown.match(frontmatterRegex);
  
  if (!match) {
    return {
      data: {},
      content: markdown,
    };
  }
  
  const frontmatterText = match[1];
  const content = match[2];
  
  const data = parseYaml(frontmatterText);
  
  return { data, content };
};

/**
 * Simple YAML parser for basic frontmatter
 * Supports: strings, numbers, booleans, arrays, and nested objects
 */
const parseYaml = (yaml) => {
  const result = {};
  const lines = yaml.split('\n');
  let currentArray = null;
  let i = 0;
  
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    
    // Skip empty lines
    if (!trimmed) {
      i++;
      continue;
    }
    
    // Check for array item
    if (trimmed.startsWith('- ')) {
      const value = parseValue(trimmed.slice(2));
      if (currentArray) {
        currentArray.push(value);
      }
      i++;
      continue;
    }
    
    // Check for inline array: key: [item1, item2]
    const inlineArrayMatch = trimmed.match(/^(\w+):\s*\[([^\]]*)\]$/);
    if (inlineArrayMatch) {
      const key = inlineArrayMatch[1];
      const arrayContent = inlineArrayMatch[2];
      result[key] = parseInlineArray(arrayContent);
      currentArray = null;
      i++;
      continue;
    }
    
    // Parse key-value pair
    const colonIndex = trimmed.indexOf(':');
    if (colonIndex === -1) {
      i++;
      continue;
    }
    
    const key = trimmed.slice(0, colonIndex).trim();
    let value = trimmed.slice(colonIndex + 1).trim();
    
    // Check if next line starts a block array
    const nextLine = lines[i + 1];
    if (nextLine && nextLine.trim().startsWith('- ')) {
      result[key] = [];
      currentArray = result[key];
      i++;
      continue;
    }
    
    currentArray = null;
    result[key] = parseValue(value);
    i++;
  }
  
  return result;
};

/**
 * Parse inline array syntax: [item1, item2, "item 3"]
 */
const parseInlineArray = (content) => {
  if (!content.trim()) return [];
  
  const result = [];
  let current = '';
  let inQuotes = false;
  let quoteChar = null;
  
  for (let i = 0; i < content.length; i++) {
    const char = content[i];
    
    if (!inQuotes && (char === '"' || char === "'")) {
      inQuotes = true;
      quoteChar = char;
      continue;
    }
    
    if (inQuotes && char === quoteChar) {
      inQuotes = false;
      quoteChar = null;
      continue;
    }
    
    if (!inQuotes && char === ',') {
      result.push(parseValue(current.trim()));
      current = '';
      continue;
    }
    
    current += char;
  }
  
  if (current.trim()) {
    result.push(parseValue(current.trim()));
  }
  
  return result;
};

/**
 * Parse a YAML value to its JavaScript equivalent
 */
const parseValue = (value) => {
  // Remove quotes if present
  if ((value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))) {
    return value.slice(1, -1);
  }
  
  // Boolean
  if (value === 'true') return true;
  if (value === 'false') return false;
  
  // Null
  if (value === 'null' || value === '~') return null;
  
  // Number
  if (/^-?\d+$/.test(value)) return parseInt(value, 10);
  if (/^-?\d+\.\d+$/.test(value)) return parseFloat(value);
  
  // String (return as-is)
  return value;
};

export default parseFrontmatter;
